/**
 * The `Pins` tab: every pinned message with its sender, two-line body,
 * pinned meta line, and an `Unpin` action visible only to a moderator.
 *
 * `canModerate` is passed down from `/chat/get`, never re-derived from
 * `myRole` (plan §5.2, §5.6). Unpinning here is treated as a
 * `destructive-confirmation.mdc` "remove-from-a-list" action (the rule's own
 * second named category), unlike the same `/message/unpin` call reached from
 * the per-message action menu (WI-W14), which the plan deliberately keeps
 * confirm-free because it fires right after pinning and is one tap to undo
 * in place. See this work item's note in `plan.md` for the exact copy this
 * component interprets rather than one the plan spelled out verbatim.
 */
import {
  useChatListPins,
  useMessageUnpin,
} from "@sportsmet/messaging-sdk/private";
import { confirmDestructive, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { pinBodyLabel, pinnedMetaLine } from "./pinDisplay";

export type typePinsTabProps = {
  chatId: string;
  canModerate: boolean;
  onJumpToMessage: (messageId: string) => void;
};

export const PinsTab = (props: typePinsTabProps): ReactNode => {
  const chatListPins = useChatListPins({ chatId: props.chatId });
  const messageUnpin = useMessageUnpin();

  const items = chatListPins.data?.data?.items ?? [];

  const handleUnpin = async (messageId: string): Promise<void> => {
    const confirmed = await confirmDestructive({
      title: "Unpin this message?",
      body: "It will come off the Pins tab and the pinned bar.",
      confirmLabel: "Unpin",
    });
    if (!confirmed) return;

    try {
      const result = await messageUnpin.mutateAsync({ messageId });
      if ((result.error ?? null) !== null || (result.data ?? null) === null) {
        showApiError(result);
      }
    } catch (error) {
      showApiError(error);
    }
  };

  if (chatListPins.isLoading) {
    return (
      <div
        data-testid="chat-details-pins-loading"
        className="flex flex-col gap-3"
      >
        <Skeleton shape="text" lines={3} label="Loading pinned messages" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        data-testid="chat-details-pins-empty"
        className="flex flex-col items-center gap-1 py-10 text-center"
      >
        <p className="text-body-strong text-ink">No pinned messages</p>
        <p className="text-body text-ink-secondary">
          Pin something important and it shows up here.
        </p>
      </div>
    );
  }

  return (
    <ul data-testid="chat-details-pins" className="flex flex-col gap-3">
      {items.map((item) => (
        <li
          key={item.messageId}
          data-testid="pin-row"
          className="flex flex-col gap-1 border-b border-hairline pb-3"
        >
          <button
            type="button"
            className="flex flex-col gap-1 text-left"
            onClick={() => {
              props.onJumpToMessage(item.messageId);
            }}
          >
            <span className="text-body-strong text-ink">
              {item.senderDisplayName}
            </span>
            <span
              data-testid="pin-row-body"
              className="line-clamp-2 text-body text-ink-secondary"
            >
              {pinBodyLabel(item)}
            </span>
            <span className="text-caption text-ink-tertiary">
              {pinnedMetaLine(
                item.pinnedAtIso,
                item.pinnedByDisplayName,
                new Date(),
              )}
            </span>
          </button>
          {props.canModerate ? (
            <button
              type="button"
              data-testid="pin-row-unpin"
              className="self-start text-body-strong text-danger"
              onClick={() => {
                void handleUnpin(item.messageId);
              }}
            >
              Unpin
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
};
